import { Button, Divider, Stack, Typography, IconButton, Tooltip, type SxProps, type Theme } from '@mui/material';
import {
  EditOutlined,
  ConnectWithoutContactOutlined,
  PlayCircleOutlined,
  ArrowRightOutlined,
  CastOutlined,
} from '@mui/icons-material';
import type { Mode } from '@/types/entity-mode';
import type { EventDto } from '@/types/dtos/events/response/event.dto';
import { EventPublish } from '@/components/events/detail/event-publish';
import { useNotifications } from '@/hooks/use-notifications';
import { useTranslations } from '@/i18n/use-translations';

export type EventModeSectionProps = {
  currentMode: Mode;
  hasPlanMode: boolean;
  hasOrganizeMode: boolean;
  handleModeChange: (mode: Mode) => void;
  event: EventDto | null;
  eventSlotIdParam?: string | null;
  onShowPublishAlert?: () => void;
  showDivider?: boolean;
  sx?: SxProps<Theme>;
  onClick?: (e: React.MouseEvent) => void;
};

export const EventModeSection = ({
  currentMode,
  hasPlanMode,
  hasOrganizeMode,
  handleModeChange,
  event,
  eventSlotIdParam = null,
  onShowPublishAlert,
  showDivider = true,
  sx,
  onClick,
}: EventModeSectionProps) => {
  const notification = useNotifications();
  const t = useTranslations();
  const hasEventSlotId = !!eventSlotIdParam;

  const handlePerformSectionClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!hasEventSlotId) {
      notification.show(
        t('flows.mode.enterStudioToPerform', { defaultValue: 'Enter Studio to take a scheduled flow live.' }),
        {
          severity: 'info',
        },
      );
    }
  };
  const handlePerformInfoClick = !hasEventSlotId ? handlePerformSectionClick : undefined;

  return (
    <>
      {showDivider && <Divider orientation="horizontal" />}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={0}
        sx={{
          height: '32px',
          minHeight: '32px',
          maxHeight: '32px',
          p: 0,
          width: '100%',
          ...sx,
        }}
        onClick={onClick || ((e) => e.stopPropagation())}
      >
        {/* PLAN mode section - only show if PLAN mode data is available */}
        {hasPlanMode ? (
          <>
            {/* PLAN mode button */}
            <Button
              variant={currentMode === 'PLAN' ? 'contained' : 'outlined'}
              color="secondary"
              size="small"
              onClick={() => handleModeChange('PLAN')}
              disabled={!hasPlanMode}
              sx={{ display: 'flex', gap: 0.5 }}
            >
              <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                1
              </Typography>
              <EditOutlined fontSize="small" />
            </Button>
            {/* ArrowRightOutlined */}
            <ArrowRightOutlined fontSize="small" color="action" />
            {/* Publish button for event */}
            {event ? <EventPublish event={event} onIndicativeClick={onShowPublishAlert} /> : null}
            {/* ArrowRightOutlined */}
            <ArrowRightOutlined fontSize="small" color="action" />
          </>
        ) : null}
        {/* ORGANIZE mode button */}
        <Button
          variant={currentMode === 'ORGANIZE' ? 'contained' : 'outlined'}
          color="secondary"
          size="small"
          onClick={() => handleModeChange('ORGANIZE')}
          disabled={!hasOrganizeMode}
          sx={{ display: 'flex', gap: 0.5 }}
        >
          <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
            2
          </Typography>
          <ConnectWithoutContactOutlined fontSize="small" />
        </Button>
        {/* ArrowRightOutlined, PlayCircleOutlined, ArrowRightOutlined, and PERFORM mode button */}
        <>
          {/* ArrowRightOutlined */}
          <Tooltip
            title={
              !hasEventSlotId
                ? t('flows.mode.enterStudioToPerform', { defaultValue: 'Enter Studio to take a scheduled flow live.' })
                : ''
            }
          >
            <span onClick={handlePerformInfoClick}>
              <IconButton
                size="small"
                disabled={!hasEventSlotId}
                onClick={handlePerformSectionClick}
                sx={{ width: '24px', height: '24px', padding: 0 }}
              >
                <ArrowRightOutlined fontSize="small" color={hasEventSlotId ? 'action' : 'disabled'} />
              </IconButton>
            </span>
          </Tooltip>
          {/* PlayCircleOutlined */}
          <Tooltip
            title={
              !hasEventSlotId
                ? t('flows.mode.enterStudioToPerform', { defaultValue: 'Enter Studio to take a scheduled flow live.' })
                : ''
            }
          >
            <span onClick={handlePerformInfoClick}>
              <IconButton
                size="small"
                disabled={!hasEventSlotId}
                onClick={handlePerformSectionClick}
                sx={{ width: '24px', height: '24px', padding: 0 }}
              >
                <PlayCircleOutlined fontSize="small" color={hasEventSlotId ? 'action' : 'disabled'} />
              </IconButton>
            </span>
          </Tooltip>
          {/* ArrowRightOutlined */}
          <Tooltip
            title={
              !hasEventSlotId
                ? t('flows.mode.enterStudioToPerform', { defaultValue: 'Enter Studio to take a scheduled flow live.' })
                : ''
            }
          >
            <span onClick={handlePerformInfoClick}>
              <IconButton
                size="small"
                disabled={!hasEventSlotId}
                onClick={handlePerformSectionClick}
                sx={{ width: '24px', height: '24px', padding: 0 }}
              >
                <ArrowRightOutlined fontSize="small" color={hasEventSlotId ? 'action' : 'disabled'} />
              </IconButton>
            </span>
          </Tooltip>
          {/* PERFORM mode button */}
          <Tooltip
            title={
              !hasEventSlotId
                ? t('flows.mode.enterStudioToPerform', { defaultValue: 'Enter Studio to take a scheduled flow live.' })
                : ''
            }
          >
            <span onClick={handlePerformInfoClick}>
              <Button
                variant={currentMode === 'PERFORM' ? 'contained' : 'outlined'}
                color={currentMode === 'PERFORM' ? 'error' : 'error'}
                size="small"
                onClick={hasEventSlotId ? () => handleModeChange('PERFORM') : handlePerformSectionClick}
                disabled={!hasEventSlotId}
                sx={{ display: 'flex', gap: 0.5 }}
              >
                <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                  3
                </Typography>
                <CastOutlined fontSize="small" />
              </Button>
            </span>
          </Tooltip>
        </>
      </Stack>
    </>
  );
};
